import { strFromU8 } from 'fflate';
import { fail } from './SongPackError.js';
import {
  CHART_DOCUMENT_DEFAULTS,
  DIFFICULTIES,
  MAX_CHART_EVENTS,
  SONG_PACK_FORMAT_VERSION,
  chartFileName,
  isDifficulty,
  type ChartDocumentV1,
  type Difficulty,
} from './schema/ChartDocumentV1.js';
import {
  COVER_FILE,
  MANIFEST_DEFAULTS,
  MUSIC_FILE,
  isValidId,
  type ChartReferenceV1,
  type ExtensionPropertyV1,
  type ManifestV1,
} from './schema/ManifestV1.js';
import {
  WAVE_EVENT_DEFAULTS,
  ZERO_COLOR,
  ZERO_VECTOR2,
  type ColorV1,
  type Vector2V1,
  type WaveEventV1,
} from './schema/WaveEventV1.js';
import { isSpawnPositionMode, type SpawnPositionMode } from './schema/SpawnPositionMode.js';

/**
 * Semantic validation for Song Pack v1 payloads.
 *
 * Every rule in Unity's `SongPackService.Validate` / `ValidateChart` /
 * `ValidateChartReferences` / `ValidateId` is reproduced here. Where this reader
 * is deliberately *stricter* than Unity, the rule is marked `EXTRA:` — those are
 * cases where Unity would accept data that cannot survive the web runtime, and
 * where requirement 9 ("reject NaN / Infinity / malformed fields") applies.
 *
 * Nothing here mutates or repairs data. A document either satisfies version 1 or
 * it is rejected; there is no coercion path.
 */

/** Per-entry byte limits, copied from Unity's `SongPackService.Read`. */
export const ENTRY_SIZE_LIMITS: Readonly<Record<string, number>> = Object.freeze({
  'manifest.json': 1024 * 1024,
  'music.wav': 512 * 1024 * 1024,
  'cover.png': 32 * 1024 * 1024,
  'easy.chart': 16 * 1024 * 1024,
  'normal.chart': 16 * 1024 * 1024,
  'hard.chart': 16 * 1024 * 1024,
});

/**
 * Cap for a bundled music video.
 *
 * Deliberately far above the others: an MV is the only asset in a Song Pack that
 * is expected to reach hundreds of megabytes, and a limit that rejected a real
 * one would just push people back to loose files in `/video`. It is still a
 * limit, because "no limit" and "512 MiB" behave very differently when the input
 * is hostile.
 */
export const VIDEO_SIZE_LIMIT = 512 * 1024 * 1024;

const RIFF = [0x52, 0x49, 0x46, 0x46] as const; // "RIFF"
const WAVE = [0x57, 0x41, 0x56, 0x45] as const; // "WAVE"
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10] as const;
/** ISO base media (MP4/MOV): a `ftyp` box at offset 4. */
const FTYP = [0x66, 0x74, 0x79, 0x70] as const;
/** Matroska/WebM EBML header. */
const EBML = [0x1a, 0x45, 0xdf, 0xa3] as const;

// ---------------------------------------------------------------------------
// Binary payloads
// ---------------------------------------------------------------------------

/**
 * `music.wav` must be RIFF/WAVE.
 *
 * Unity: `music.Length < 12 || ASCII[0..4] != "RIFF" || ASCII[8..12] != "WAVE"`.
 */
export function validateMusicBytes(bytes: Uint8Array): void {
  if (bytes.length < 12 || !matches(bytes, 0, RIFF) || !matches(bytes, 8, WAVE)) {
    fail('INVALID_MUSIC', 'music.wav is not RIFF/WAVE.', { entry: MUSIC_FILE });
  }
}

/**
 * Song length, read straight out of the RIFF header.
 *
 * Song Select shows a duration for every song in the library, and decoding a
 * 20 MB WAV per song to find out how long it is would make opening the library
 * cost as much as playing it. Version 1's music is always PCM WAV, where the
 * length is arithmetic on two header fields.
 *
 * Returns 0 when the header cannot be read that way. A missing duration is a
 * blank field; a wrong one would be a lie, and this is not the layer that gets
 * to guess.
 */
export function readWavDurationSeconds(bytes: Uint8Array): number {
  if (bytes.length < 44) return 0;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let channels = 0;
  let sampleRate = 0;
  let bitsPerSample = 0;
  let dataBytes = 0;

  // Chunks are walked rather than assumed at fixed offsets: encoders insert
  // `LIST`/`fact` chunks before `data` often enough that a hard-coded offset
  // reads the wrong number rather than failing.
  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const id = String.fromCharCode(
      bytes[offset] as number,
      bytes[offset + 1] as number,
      bytes[offset + 2] as number,
      bytes[offset + 3] as number,
    );
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ' && body + 16 <= bytes.length) {
      channels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      bitsPerSample = view.getUint16(body + 14, true);
    } else if (id === 'data') {
      // The declared size can overrun a truncated file; the real bytes win.
      dataBytes = Math.min(size, Math.max(0, bytes.length - body));
      break;
    }
    // Chunks are word-aligned, and a zero size would spin forever.
    offset = body + size + (size % 2);
    if (size === 0) break;
  }

  const bytesPerFrame = channels * Math.floor(bitsPerSample / 8);
  if (bytesPerFrame <= 0 || sampleRate <= 0 || dataBytes <= 0) return 0;
  return dataBytes / bytesPerFrame / sampleRate;
}

/** `cover.png` must carry the PNG signature. Unity applies the same eight bytes. */
export function validateCoverBytes(bytes: Uint8Array): void {
  if (bytes.length < PNG_SIGNATURE.length || !matches(bytes, 0, PNG_SIGNATURE)) {
    fail('INVALID_COVER', 'cover.png is not a PNG.', { entry: COVER_FILE });
  }
}

/**
 * A bundled music video must be something a `<video>` element can decode.
 *
 * Only the container is checked, which is all that can be checked cheaply and
 * all that is worth checking: whether a given MP4's codecs play is a question
 * for the browser, and the answer differs between browsers. What this rejects is
 * a file that is not a video at all — a renamed ZIP, an HTML error page, a
 * truncated download — which is the case that would otherwise become a silent
 * "Mirror Drift is off" with no explanation.
 */
export function validateVideoBytes(bytes: Uint8Array, entry: string): void {
  const isMp4 = bytes.length >= 12 && matches(bytes, 4, FTYP);
  const isWebm = bytes.length >= 4 && matches(bytes, 0, EBML);
  if (!isMp4 && !isWebm) {
    fail('INVALID_VIDEO', `${entry} is not an MP4 or WebM video.`, { entry });
  }
}

function matches(bytes: Uint8Array, offset: number, signature: readonly number[]): boolean {
  for (let index = 0; index < signature.length; index += 1) {
    if (bytes[offset + index] !== signature[index]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// JSON decoding
// ---------------------------------------------------------------------------

/**
 * Decodes a UTF-8 JSON entry.
 *
 * Note on non-finite numbers: standard JSON has no `NaN` / `Infinity` literals, so
 * a producer emitting them yields a syntax error here rather than a value check.
 * `1e999` is the reachable route to `Infinity` through *valid* JSON, and that is
 * caught by the numeric readers below.
 */
export function decodeJson(bytes: Uint8Array, entry: string): unknown {
  let text: string;
  try {
    text = strFromU8(bytes);
  } catch (cause) {
    fail('INVALID_JSON', `${entry} is not valid UTF-8.`, { entry, cause });
  }
  try {
    return JSON.parse(text) as unknown;
  } catch (cause) {
    fail('INVALID_JSON', `${entry} is not valid JSON.`, { entry, cause });
  }
}

// ---------------------------------------------------------------------------
// manifest.json
// ---------------------------------------------------------------------------

export function parseManifest(raw: unknown): ManifestV1 {
  const source = asRecord(raw, 'INVALID_MANIFEST', 'manifest.json is not a JSON object.', {
    entry: 'manifest.json',
  });
  const context = { entry: 'manifest.json' };

  const formatVersion = readNumber(source, 'formatVersion', SONG_PACK_FORMAT_VERSION, context);
  if (formatVersion !== SONG_PACK_FORMAT_VERSION) {
    fail(
      'UNSUPPORTED_FORMAT_VERSION',
      `Unsupported Song Pack version ${formatVersion}; only version ${SONG_PACK_FORMAT_VERSION} is supported.`,
      context,
    );
  }

  const packId = readString(source, 'packId', '', context);
  const songId = readString(source, 'songId', '', context);
  if (!isValidId(packId)) {
    fail('INVALID_ID', 'manifest packId is empty, too long, or contains an invalid character.', {
      ...context,
      path: 'packId',
    });
  }
  if (!isValidId(songId)) {
    fail('INVALID_ID', 'manifest songId is empty, too long, or contains an invalid character.', {
      ...context,
      path: 'songId',
    });
  }

  const displayName = readString(source, 'displayName', '', context);
  if (displayName.length === 0) {
    fail('INVALID_MANIFEST', 'manifest displayName is required.', {
      ...context,
      path: 'displayName',
    });
  }

  const bpm = readNumber(source, 'bpm', MANIFEST_DEFAULTS.bpm, context, 'bpm');
  if (bpm <= 0) {
    fail('INVALID_MANIFEST', 'manifest bpm must be greater than zero.', {
      ...context,
      path: 'bpm',
    });
  }

  const musicFile = readString(source, 'musicFile', MANIFEST_DEFAULTS.musicFile, context);
  const coverFile = readString(source, 'coverFile', MANIFEST_DEFAULTS.coverFile, context);
  // Unity compares these with `StringComparer.Ordinal`, i.e. case-sensitively.
  if (musicFile !== MUSIC_FILE) {
    fail('INVALID_MANIFEST', `Version 1 requires musicFile to be "${MUSIC_FILE}".`, {
      ...context,
      path: 'musicFile',
    });
  }
  if (coverFile.length > 0 && coverFile !== COVER_FILE) {
    fail('INVALID_MANIFEST', `Version 1 requires coverFile to be "${COVER_FILE}" or empty.`, {
      ...context,
      path: 'coverFile',
    });
  }

  const charts = parseChartReferences(source['charts']);

  return {
    formatVersion,
    packId,
    songId,
    displayName,
    bpm,
    endGraceSeconds: readNumber(
      source,
      'endGraceSeconds',
      MANIFEST_DEFAULTS.endGraceSeconds,
      context,
      'endGraceSeconds',
    ),
    dspLeadTime: readNumber(
      source,
      'dspLeadTime',
      MANIFEST_DEFAULTS.dspLeadTime,
      context,
      'dspLeadTime',
    ),
    musicFile,
    coverFile,
    charts,
    author: readString(source, 'author', MANIFEST_DEFAULTS.author, context),
    description: readString(source, 'description', MANIFEST_DEFAULTS.description, context),
    tags: parseStringArray(source['tags'], 'tags'),
    extensions: parseExtensions(source['extensions']),
  };
}

/**
 * Version 1 requires exactly three references, one per difficulty, each pointing
 * at the canonical file name. Unity's `ValidateChartReferences` matches the
 * difficulty case-insensitively but the file name case-sensitively.
 */
function parseChartReferences(raw: unknown): readonly ChartReferenceV1[] {
  if (!Array.isArray(raw)) {
    fail('INVALID_CHART_REFERENCE', 'manifest charts must be an array.', {
      entry: 'manifest.json',
      path: 'charts',
    });
  }
  if (raw.length !== DIFFICULTIES.length) {
    fail(
      'INVALID_CHART_REFERENCE',
      `Version 1 requires exactly ${DIFFICULTIES.length} chart references.`,
      { entry: 'manifest.json', path: 'charts' },
    );
  }

  const references: ChartReferenceV1[] = raw.map((entry, index) => {
    const record = asRecord(
      entry,
      'INVALID_CHART_REFERENCE',
      'A chart reference is not a JSON object.',
      { entry: 'manifest.json', path: `charts[${index}]` },
    );
    return {
      difficulty: readString(record, 'difficulty', '', {
        entry: 'manifest.json',
        path: `charts[${index}].difficulty`,
      }),
      file: readString(record, 'file', '', {
        entry: 'manifest.json',
        path: `charts[${index}].file`,
      }),
    };
  });

  for (const difficulty of DIFFICULTIES) {
    const matching = references.filter(
      (reference) => reference.difficulty.toLowerCase() === difficulty,
    );
    if (matching.length !== 1 || matching[0]?.file !== chartFileName(difficulty)) {
      fail('INVALID_CHART_REFERENCE', `Invalid ${difficulty} chart reference.`, {
        entry: 'manifest.json',
        path: 'charts',
      });
    }
  }
  return references;
}

function parseExtensions(raw: unknown): readonly ExtensionPropertyV1[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    fail('INVALID_MANIFEST', 'manifest extensions must be an array.', {
      entry: 'manifest.json',
      path: 'extensions',
    });
  }
  return raw.map((entry, index) => {
    const record = asRecord(
      entry,
      'INVALID_MANIFEST',
      'An extension property is not a JSON object.',
      { entry: 'manifest.json', path: `extensions[${index}]` },
    );
    return {
      key: readString(record, 'key', '', {
        entry: 'manifest.json',
        path: `extensions[${index}].key`,
      }),
      value: readString(record, 'value', '', {
        entry: 'manifest.json',
        path: `extensions[${index}].value`,
      }),
    };
  });
}

function parseStringArray(raw: unknown, path: string): readonly string[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    fail('INVALID_MANIFEST', `manifest ${path} must be an array.`, {
      entry: 'manifest.json',
      path,
    });
  }
  return raw.map((entry, index) => {
    if (typeof entry !== 'string') {
      fail('INVALID_MANIFEST', `manifest ${path} must contain only strings.`, {
        entry: 'manifest.json',
        path: `${path}[${index}]`,
      });
    }
    return entry;
  });
}

// ---------------------------------------------------------------------------
// *.chart
// ---------------------------------------------------------------------------

export function parseChartDocument(
  raw: unknown,
  expected: Difficulty,
  entry: string,
): ChartDocumentV1 {
  const source = asRecord(raw, 'INVALID_CHART', `${entry} is not a JSON object.`, { entry });
  const context = { entry };

  // Version first: an unknown version must never be interpreted (requirement 8).
  const formatVersion = readNumber(source, 'formatVersion', SONG_PACK_FORMAT_VERSION, context);
  if (formatVersion !== SONG_PACK_FORMAT_VERSION) {
    fail(
      'UNSUPPORTED_FORMAT_VERSION',
      `Unsupported chart version ${formatVersion}; only version ${SONG_PACK_FORMAT_VERSION} is supported.`,
      context,
    );
  }

  const difficultyRaw = readString(source, 'difficulty', '', context);
  const difficulty = difficultyRaw.toLowerCase();
  if (!isDifficulty(difficulty) || difficulty !== expected) {
    fail('INVALID_CHART', `Invalid chart: ${entry} declares difficulty "${difficultyRaw}".`, {
      ...context,
      path: 'difficulty',
    });
  }

  const eventsRaw = source['events'];
  if (eventsRaw !== undefined && eventsRaw !== null && !Array.isArray(eventsRaw)) {
    fail('INVALID_CHART', 'Chart events must be an array.', { ...context, path: 'events' });
  }
  const eventList: unknown[] = Array.isArray(eventsRaw) ? eventsRaw : [];
  if (eventList.length > MAX_CHART_EVENTS) {
    fail('TOO_MANY_EVENTS', `Chart declares more than ${MAX_CHART_EVENTS} events.`, {
      ...context,
      path: 'events',
    });
  }

  const events: WaveEventV1[] = new Array<WaveEventV1>(eventList.length);
  for (let index = 0; index < eventList.length; index += 1) {
    events[index] = parseWaveEvent(eventList[index], index, entry);
  }

  return {
    formatVersion,
    difficulty,
    bpm: readNumber(source, 'bpm', CHART_DOCUMENT_DEFAULTS.bpm, context, 'bpm'),
    detectedBpm: readNumber(
      source,
      'detectedBpm',
      CHART_DOCUMENT_DEFAULTS.detectedBpm,
      context,
      'detectedBpm',
    ),
    firstBeatOffsetSeconds: readNumber(
      source,
      'firstBeatOffsetSeconds',
      CHART_DOCUMENT_DEFAULTS.firstBeatOffsetSeconds,
      context,
      'firstBeatOffsetSeconds',
    ),
    detectedBeatTimes: readNumberArray(source['detectedBeatTimes'], 'detectedBeatTimes', entry),
    events,
    poolCapacity: readInteger(
      source,
      'poolCapacity',
      CHART_DOCUMENT_DEFAULTS.poolCapacity,
      context,
      'poolCapacity',
    ),
    positionRerollLimit: readInteger(
      source,
      'positionRerollLimit',
      CHART_DOCUMENT_DEFAULTS.positionRerollLimit,
      context,
      'positionRerollLimit',
    ),
    minimumDistanceFromPreviousOrigin: readNumber(
      source,
      'minimumDistanceFromPreviousOrigin',
      CHART_DOCUMENT_DEFAULTS.minimumDistanceFromPreviousOrigin,
      context,
      'minimumDistanceFromPreviousOrigin',
    ),
    randomSeed: readInteger(
      source,
      'randomSeed',
      CHART_DOCUMENT_DEFAULTS.randomSeed,
      context,
      'randomSeed',
    ),
  };
}

function parseWaveEvent(raw: unknown, index: number, entry: string): WaveEventV1 {
  const source = asRecord(raw, 'INVALID_EVENT', 'A chart event is not a JSON object.', {
    entry,
    path: `events[${index}]`,
  });
  const at = (field: string) => ({ entry, path: `events[${index}].${field}` });

  const time = readNumber(source, 'time', WAVE_EVENT_DEFAULTS.time, { entry }, `events[${index}].time`);
  // Unity: `!IsFinite(time) || time < 0f` is rejected.
  if (time < 0) {
    fail('INVALID_EVENT', 'Chart event time must not be negative.', at('time'));
  }
  const beat = readNumber(source, 'beat', WAVE_EVENT_DEFAULTS.beat, { entry }, `events[${index}].beat`);
  if (beat < 0) {
    fail('INVALID_EVENT', 'Chart event beat must not be negative.', at('beat'));
  }

  const spawnPositionModeRaw = readNumber(
    source,
    'spawnPositionMode',
    WAVE_EVENT_DEFAULTS.spawnPositionMode,
    { entry },
    `events[${index}].spawnPositionMode`,
  );
  // EXTRA: Unity deserialises any integer into the enum and falls through to the
  // `default` switch arm. An out-of-range ordinal means the chart was produced by
  // something that does not agree with this contract, so it is rejected here.
  if (!isSpawnPositionMode(spawnPositionModeRaw)) {
    fail('INVALID_EVENT', `Unknown spawnPositionMode ${spawnPositionModeRaw}.`, {
      ...at('spawnPositionMode'),
    });
  }
  const spawnPositionMode: SpawnPositionMode = spawnPositionModeRaw;

  const number = (field: keyof typeof WAVE_EVENT_DEFAULTS): number =>
    readNumber(source, field, WAVE_EVENT_DEFAULTS[field], { entry }, `events[${index}].${field}`);
  const integer = (field: keyof typeof WAVE_EVENT_DEFAULTS): number =>
    readInteger(source, field, WAVE_EVENT_DEFAULTS[field], { entry }, `events[${index}].${field}`);

  return {
    time,
    beat,
    initialRadius: number('initialRadius'),
    intensity: number('intensity'),
    spawnPositionMode,
    origin: readVector2(source, 'origin', entry, index),
    fixedWorldPosition: readVector2(source, 'fixedWorldPosition', entry, index),
    normalizedPosition: readVector2(source, 'normalizedPosition', entry, index),
    edgePadding: number('edgePadding'),
    minimumDistanceFromPlayer: number('minimumDistanceFromPlayer'),
    randomSeedOffset: integer('randomSeedOffset'),
    warningLeadTime: number('warningLeadTime'),
    speed: number('speed'),
    gateAngle: number('gateAngle'),
    gateCenterAngle: number('gateCenterAngle'),
    gateWidth: number('gateWidth'),
    damage: integer('damage'),
    baseScore: integer('baseScore'),
    dangerColor: readColor(source, 'dangerColor', entry, index),
    gateColor: readColor(source, 'gateColor', entry, index),
    paintColor: readColor(source, 'paintColor', entry, index),
  };
}

// ---------------------------------------------------------------------------
// Cross-entry rules
// ---------------------------------------------------------------------------

/**
 * Confirms that the archive carries a cover exactly when the manifest claims one.
 * Unity reads `cover.png` only when `manifest.coverFile` is non-empty, so a cover
 * present in the ZIP but unreferenced is ignored by both readers.
 */
export function expectsCover(manifest: ManifestV1): boolean {
  return manifest.coverFile.length > 0;
}

// ---------------------------------------------------------------------------
// Field readers
// ---------------------------------------------------------------------------

interface Context {
  readonly entry?: string;
  readonly path?: string;
}

function asRecord(
  value: unknown,
  code: 'INVALID_MANIFEST' | 'INVALID_CHART' | 'INVALID_EVENT' | 'INVALID_CHART_REFERENCE',
  message: string,
  context: Context,
): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail(code, message, context);
  }
  return value as Record<string, unknown>;
}

/**
 * Reads a numeric field.
 *
 * An absent or null key falls back to `fallback`, matching how `JsonUtility` and
 * the Python dataclasses leave unset fields at their declared default. A present
 * key must be a finite number: `1e999` parses as `Infinity` through entirely valid
 * JSON, which is the practical route by which a non-finite value reaches us.
 */
function readNumber(
  source: Record<string, unknown>,
  key: string,
  fallback: number,
  context: Context,
  path?: string,
): number {
  const value = source[key];
  if (value === undefined || value === null) return fallback;
  const where: Context = { ...context, path: path ?? key };
  if (typeof value !== 'number') {
    fail('INVALID_EVENT', `Expected a number for "${key}".`, where);
  }
  if (!Number.isFinite(value)) {
    fail('NON_FINITE_VALUE', `"${key}" is NaN or Infinity.`, where);
  }
  return value;
}

const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;

/**
 * Reads a field that the contract types as a C# `int`.
 *
 * EXTRA: Unity relies on `JsonUtility` coercion here and would silently truncate.
 * The compiled chart stores these in `Int32Array`, where an out-of-range value
 * would wrap into a different number, so a non-integral or out-of-range value is
 * rejected rather than quietly corrupted. `1.0` still reads as the integer 1,
 * which is what both producers emit.
 */
function readInteger(
  source: Record<string, unknown>,
  key: string,
  fallback: number,
  context: Context,
  path?: string,
): number {
  const value = readNumber(source, key, fallback, context, path);
  const where: Context = { ...context, path: path ?? key };
  if (!Number.isInteger(value)) {
    fail('INVALID_EVENT', `"${key}" must be an integer.`, where);
  }
  if (value < INT32_MIN || value > INT32_MAX) {
    fail('INVALID_EVENT', `"${key}" is outside the 32-bit integer range.`, where);
  }
  return value;
}

function readString(
  source: Record<string, unknown>,
  key: string,
  fallback: string,
  context: Context,
): string {
  const value = source[key];
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'string') {
    fail('INVALID_MANIFEST', `Expected a string for "${key}".`, { ...context, path: key });
  }
  return value;
}

function readNumberArray(raw: unknown, key: string, entry: string): readonly number[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    fail('INVALID_CHART', `"${key}" must be an array.`, { entry, path: key });
  }
  const result = new Array<number>(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    const value = raw[index];
    if (typeof value !== 'number') {
      fail('INVALID_CHART', `"${key}" must contain only numbers.`, {
        entry,
        path: `${key}[${index}]`,
      });
    }
    if (!Number.isFinite(value)) {
      fail('NON_FINITE_VALUE', `"${key}" contains NaN or Infinity.`, {
        entry,
        path: `${key}[${index}]`,
      });
    }
    result[index] = value;
  }
  return result;
}

/** Reads a `{x,y}` pair. Both producers use this shape for `Vector2`. */
function readVector2(
  source: Record<string, unknown>,
  key: string,
  entry: string,
  index: number,
): Vector2V1 {
  const value = source[key];
  if (value === undefined || value === null) return ZERO_VECTOR2;
  const record = asRecord(value, 'INVALID_EVENT', `"${key}" must be a {x,y} object.`, {
    entry,
    path: `events[${index}].${key}`,
  });
  const context: Context = { entry };
  return {
    x: readNumber(record, 'x', 0, context, `events[${index}].${key}.x`),
    y: readNumber(record, 'y', 0, context, `events[${index}].${key}.y`),
  };
}

/**
 * Reads an `{r,g,b,a}` colour.
 *
 * The Python engine declares colour fields with `default_factory=dict`, so a
 * generator that leaves one unset emits `{}`. That is read as Unity's zeroed
 * `Color`, which the view layer already interprets as "use the theme default".
 * Substituting a real colour here would modify chart content (requirement 12).
 */
function readColor(
  source: Record<string, unknown>,
  key: string,
  entry: string,
  index: number,
): ColorV1 {
  const value = source[key];
  if (value === undefined || value === null) return ZERO_COLOR;
  const record = asRecord(value, 'INVALID_EVENT', `"${key}" must be a {r,g,b,a} object.`, {
    entry,
    path: `events[${index}].${key}`,
  });
  const context: Context = { entry };
  return {
    r: readNumber(record, 'r', 0, context, `events[${index}].${key}.r`),
    g: readNumber(record, 'g', 0, context, `events[${index}].${key}.g`),
    b: readNumber(record, 'b', 0, context, `events[${index}].${key}.b`),
    a: readNumber(record, 'a', 0, context, `events[${index}].${key}.a`),
  };
}
